import { useState, type CSSProperties } from "react";
import { env } from "@/config/env";
import { cn } from "@/lib/cn";

/**
 * Editorial photography with the site's black-and-white treatment (people-first, as in the
 * design reference). Until the real photo is added at `src`, a duotone composition in the brand
 * colours fills the frame, so the layout always looks finished. Never a stock or invented image.
 */
export function PhotoSlot({
  src,
  alt,
  label,
  tone = "evergreen",
  className,
  mono = true,
  eager = false,
  focus = "50% 35%",
}: {
  src: string;
  alt: string;
  /** Word set in the fallback artwork (decorative). */
  label?: string;
  tone?: "evergreen" | "flame" | "leaf";
  className?: string;
  mono?: boolean;
  eager?: boolean;
  focus?: string;
}) {
  const [state, setState] = useState<"loading" | "loaded" | "error">("loading");
  return (
    <figure className={cn("photo-slot", `photo-slot--${tone}`, mono && "photo-slot--mono", state === "loaded" && "is-loaded", className)}>
      {state !== "error" ? (
        <img
          src={src}
          alt={alt}
          loading={eager ? "eager" : "lazy"}
          decoding="async"
          className="photo-slot__img"
          style={{ objectPosition: focus } as CSSProperties}
          onLoad={() => setState("loaded")}
          onError={() => setState("error")}
        />
      ) : null}
      {state !== "loaded" ? (
        <span className="photo-slot__art" aria-hidden>
          <span className="photo-slot__glow" />
          <span className="photo-slot__lines" />
          {label ? <span className="photo-slot__word">{label}</span> : null}
          {env.isDev && state === "error" ? <span className="photo-slot__hint">{src}</span> : null}
        </span>
      ) : null}
    </figure>
  );
}
